import { ClimateService } from '../../services/ClimateService';
import TierAccessService from '../../services/TierAccessService';
import store from '../../state';
import { Chart, registerables } from 'chart.js';
import { ClimatePointData } from '../../types';

Chart.register(...registerables);

export class ClimateModal {
  private static instance: ClimateModal | null = null;
  private container: HTMLElement;
  private charts: Chart[] = [];
  private currentLat: number = 0;
  private currentLon: number = 0;
  private currentElev: number = 0;
  private currentTitle: string = '';
  private unsubscribeStore: (() => void) | null = null;

  private constructor() {
    this.container = document.createElement('div');
    this.container.id = 'climate-modal-container';
    this.container.style.display = 'none';
    this.container.style.position = 'fixed';
    this.container.style.top = '0';
    this.container.style.left = '0';
    this.container.style.width = '100vw';
    this.container.style.height = '100vh';
    this.container.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    this.container.style.backdropFilter = 'blur(10px)';
    (this.container.style as any).WebkitBackdropFilter = 'blur(10px)';
    this.container.style.zIndex = '10000';
    this.container.style.alignItems = 'center';
    this.container.style.justifyContent = 'center';

    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });

    document.body.appendChild(this.container);
  }

  public static getInstance(): ClimateModal {
    if (!ClimateModal.instance) {
      ClimateModal.instance = new ClimateModal();
    }
    return ClimateModal.instance;
  }

  public close() {
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    this.container.style.display = 'none';
    this.container.innerHTML = '';
    this.charts.forEach(c => c.destroy());
    this.charts = [];
  }

  public async open(lat: number, lon: number, elev: number, title?: string) {
    this.currentLat = lat;
    this.currentLon = lon;
    this.currentElev = elev;
    this.currentTitle = title || `Location (${lat.toFixed(4)}°, ${lon.toFixed(4)}°)`;

    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    let lastTier = TierAccessService.getUserAccessTier(store.getState());
    let lastToken = store.getState().sessionToken;
    this.unsubscribeStore = store.subscribe((newState) => {
      if (this.container.style.display === 'none') return;
      const nextTier = TierAccessService.getUserAccessTier(newState);
      if (nextTier !== lastTier || newState.sessionToken !== lastToken) {
        lastTier = nextTier;
        lastToken = newState.sessionToken;
        void this.open(this.currentLat, this.currentLon, this.currentElev, this.currentTitle);
      }
    });

    this.container.style.display = 'flex';
    this.renderSkeletonHtml();

    const state = store.getState();
    const poi = state.selectedPoi;
    const minElev = poi?.minElev !== undefined ? poi.minElev : Math.max(0, elev - 1200);
    const maxElev = poi?.maxElev !== undefined ? poi.maxElev : (elev + 1800);

    const data = await ClimateService.fetchClimatePointData(state.manifest!, lat, lon, elev, minElev, maxElev);
    if (!data) {
      this.renderNoDataHtml();
      return;
    }

    this.populateData(data);
  }

  private renderSkeletonHtml() {
    const isImperial = store.getState().units === 'imperial';
    const elevDisplay = isImperial ? `${Math.round(this.currentElev)} ft` : `${Math.round(this.currentElev / 3.28084)} m`;

    this.container.innerHTML = `
      <div style="background: rgba(15, 23, 42, 0.95); border: 1px solid rgba(255,255,255,0.15); border-radius: 12px; width: 92%; max-width: 1200px; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 20px 50px rgba(0,0,0,0.6); overflow: hidden; color: #f8fafc;">
        
        <!-- Modal Header -->
        <div style="padding: 16px 20px; border-bottom: 1px solid rgba(255,255,255,0.1); display: flex; justify-content: space-between; align-items: center; background: rgba(30, 41, 59, 0.5);">
          <div>
            <h2 style="margin: 0; font-size: 1.25rem; font-weight: 800; color: #38bdf8; display: flex; align-items: center; gap: 8px;">
              <span>📊</span> 30-Year Climatology & Climate Normals
            </h2>
            <div style="font-size: 0.8rem; color: #94a3b8; margin-top: 4px;">
              ${this.currentTitle} • Lat ${this.currentLat.toFixed(4)}°, Lon ${this.currentLon.toFixed(4)}° • Elev: ${elevDisplay} (1991–2020 Baseline)
            </div>
          </div>
          <button id="climate-modal-close-btn" style="background: rgba(255,255,255,0.1); border: none; color: #fff; border-radius: 6px; padding: 6px 14px; font-size: 1rem; cursor: pointer; transition: background 0.2s;">✕ Close</button>
        </div>

        <!-- Modal Body (Scrollable) -->
        <div id="climate-modal-body" style="padding: 20px; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 20px;">
          <div style="text-align: center; padding: 40px; color: #38bdf8; font-size: 1.1rem;">
            Loading Climatology dataset (1 HTTP request)...
          </div>
        </div>

      </div>
    `;

    document.getElementById('climate-modal-close-btn')?.addEventListener('click', () => this.close());
  }

  private renderNoDataHtml() {
    const body = document.getElementById('climate-modal-body');
    if (!body) return;
    if (ClimateService.lastAccessDenial || !TierAccessService.userHasLayerAccess('climate_data')) {
      body.innerHTML = `
        <div style="max-width: 540px; margin: 36px auto; width: 100%;">
          ${TierAccessService.buildTrendChartDenialHtml({
            denial: ClimateService.lastAccessDenial,
            requiredTier: 'registered',
            featureLabel: '30-year climatology & snowpack trend charts'
          })}
        </div>
      `;
      return;
    }
    body.innerHTML = `
      <div style="text-align: center; padding: 60px; color: #ef4444; font-size: 1.1rem;">
        ⚠️ Climatology data is not available for this coordinate.
      </div>
    `;
  }

  private populateData(data: ClimatePointData) {
    const body = document.getElementById('climate-modal-body');
    if (!body) return;

    const isImperial = store.getState().units === 'imperial';
    const fmtElev = (ft: number) => isImperial ? `${Math.round(ft).toLocaleString()} ft` : `${Math.round(ft / 3.28084).toLocaleString()} m`;
    const prof = data.elevationProfile;

    body.innerHTML = `
      <!-- Top Section: 3-Column Elevation Microclimate Comparison -->
      <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 16px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 8px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 1.1rem;">⛰️</span>
            <h3 style="margin: 0; font-size: 0.95rem; color: #f8fafc;">Microclimate Elevation Variance Modeling</h3>
          </div>
          <span title="Environmental Lapse Rate: Estimates use standard -3.56°F per 1,000 ft elevation gain and orographic precipitation enhancement (+5% per 1,000 ft)." style="cursor: help; font-size: 0.85rem; background: rgba(56, 189, 248, 0.2); border: 1px solid rgba(56, 189, 248, 0.4); color: #38bdf8; border-radius: 50%; width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; font-weight: bold;">ℹ️</span>
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; font-size: 0.82rem;">
          <!-- Column 1: Valley Floor (Min) -->
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 12px; display: flex; flex-direction: column; gap: 6px;">
            <div style="font-weight: 800; color: #38bdf8; font-size: 0.88rem; border-bottom: 1px solid rgba(56,189,248,0.2); padding-bottom: 4px;">Valley Floor (Min)</div>
            <div style="color: #94a3b8; font-size: 0.75rem;">Altitude: <span style="color: #fff; font-weight: bold;">${fmtElev(prof.minElevFt)}</span></div>
            <div>Avg High: <span style="color: #fb923c; font-weight: bold;">${prof.valleyAdj.tmaxF}°F</span></div>
            <div>Avg Low: <span style="color: #38bdf8; font-weight: bold;">${prof.valleyAdj.tminF}°F</span></div>
            <div>Annual Precip: <span style="color: #60a5fa; font-weight: bold;">${prof.valleyAdj.precipInches}"</span> <span style="font-size: 0.70rem; color: #94a3b8;">(${Math.round(prof.valleyAdj.precipMult * 100)}%)</span></div>
            <div>Annual Snow: <span style="color: #c084fc; font-weight: bold;">${prof.valleyAdj.snowInches}"</span></div>
            <div>Peak Snowpack: <span style="color: #e2e8f0; font-weight: bold;">${prof.valleyAdj.snowpackInches}"</span></div>
          </div>

          <!-- Column 2: Grid Baseline (Mean) -->
          <div style="background: rgba(30, 41, 59, 0.7); border: 1px solid rgba(56,189,248,0.3); border-radius: 6px; padding: 12px; display: flex; flex-direction: column; gap: 6px; box-shadow: 0 0 12px rgba(56,189,248,0.15);">
            <div style="font-weight: 800; color: #f8fafc; font-size: 0.88rem; border-bottom: 1px solid rgba(255,255,255,0.2); padding-bottom: 4px; display: flex; justify-content: space-between;">
              <span>Grid Baseline</span>
              <span style="font-size: 0.65rem; background: #3b82f6; color: #fff; padding: 1px 5px; border-radius: 3px;">BASE</span>
            </div>
            <div style="color: #94a3b8; font-size: 0.75rem;">Altitude: <span style="color: #fff; font-weight: bold;">${fmtElev(prof.baseElevFt)}</span></div>
            <div>Avg High: <span style="color: #fb923c; font-weight: bold;">${prof.baseAdj.tmaxF}°F</span></div>
            <div>Avg Low: <span style="color: #38bdf8; font-weight: bold;">${prof.baseAdj.tminF}°F</span></div>
            <div>Annual Precip: <span style="color: #60a5fa; font-weight: bold;">${prof.baseAdj.precipInches}"</span> <span style="font-size: 0.70rem; color: #94a3b8;">(100%)</span></div>
            <div>Annual Snow: <span style="color: #c084fc; font-weight: bold;">${prof.baseAdj.snowInches}"</span></div>
            <div>Peak Snowpack: <span style="color: #e2e8f0; font-weight: bold;">${prof.baseAdj.snowpackInches}"</span></div>
          </div>

          <!-- Column 3: Ridge Summit (Max) -->
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 12px; display: flex; flex-direction: column; gap: 6px;">
            <div style="font-weight: 800; color: #f43f5e; font-size: 0.88rem; border-bottom: 1px solid rgba(244,63,94,0.2); padding-bottom: 4px;">Ridge Summit (Max)</div>
            <div style="color: #94a3b8; font-size: 0.75rem;">Altitude: <span style="color: #fff; font-weight: bold;">${fmtElev(prof.maxElevFt)}</span></div>
            <div>Avg High: <span style="color: #fb923c; font-weight: bold;">${prof.peakAdj.tmaxF}°F</span></div>
            <div>Avg Low: <span style="color: #38bdf8; font-weight: bold;">${prof.peakAdj.tminF}°F</span></div>
            <div>Annual Precip: <span style="color: #60a5fa; font-weight: bold;">${prof.peakAdj.precipInches}"</span> <span style="font-size: 0.70rem; color: #94a3b8;">(${Math.round(prof.peakAdj.precipMult * 100)}%)</span></div>
            <div>Annual Snow: <span style="color: #c084fc; font-weight: bold;">${prof.peakAdj.snowInches}"</span></div>
            <div>Peak Snowpack: <span style="color: #e2e8f0; font-weight: bold;">${prof.peakAdj.snowpackInches}"</span></div>
          </div>
        </div>
      </div>

      <!-- Middle Charts Grid: 365-Day Temperature & 52-Week Snowpack -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
        <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 14px;">
          <h3 style="margin: 0 0 12px 0; color: #f8fafc; font-size: 0.92rem;">🗓️ 365-Day Annual Climatological Cycle</h3>
          <div style="height: 240px; position: relative;">
            <canvas id="climate-modal-365-chart"></canvas>
          </div>
        </div>

        <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 14px;">
          <h3 style="margin: 0 0 12px 0; color: #f8fafc; font-size: 0.92rem;">❄️ 52-Week Snowpack & SWE Progression</h3>
          <div style="height: 240px; position: relative;">
            <canvas id="climate-modal-snowpack-chart"></canvas>
          </div>
        </div>
      </div>

      <!-- Bottom Table: 12-Month Normals Breakdown -->
      <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 14px; overflow-x: auto;">
        <h3 style="margin: 0 0 12px 0; color: #f8fafc; font-size: 0.92rem;">📅 12-Month Climate Normals (1991–2020 30-Yr Baseline)</h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 0.78rem; text-align: right;">
          <thead>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.15); color: #94a3b8;">
              <th style="text-align: left; padding: 6px 8px;">Month</th>
              <th style="padding: 6px 8px;">Precip (in)</th>
              <th style="padding: 6px 8px;">Snow (in)</th>
              <th style="padding: 6px 8px;">Precip Days</th>
              <th style="padding: 6px 8px;">Days >90°F</th>
              <th style="padding: 6px 8px;">Days >100°F</th>
              <th style="padding: 6px 8px;">Days <30°F</th>
              <th style="padding: 6px 8px;">Days <0°F</th>
              <th style="padding: 6px 8px;">Avg Wind (mph)</th>
            </tr>
          </thead>
          <tbody>
            ${data.months.map(m => `
              <tr style="border-bottom: 1px solid rgba(255,255,255,0.05); color: #e2e8f0;">
                <td style="text-align: left; padding: 6px 8px; font-weight: bold; color: #38bdf8;">${m.monthName}</td>
                <td style="padding: 6px 8px; color: #60a5fa;">${m.precipInches}"</td>
                <td style="padding: 6px 8px; color: #c084fc;">${m.snowInches}"</td>
                <td style="padding: 6px 8px;">${m.precipDays} d</td>
                <td style="padding: 6px 8px; color: ${m.daysGt90 > 0 ? '#fb923c' : '#94a3b8'};">${m.daysGt90} d</td>
                <td style="padding: 6px 8px; color: ${m.daysGt100 > 0 ? '#ef4444' : '#94a3b8'};">${m.daysGt100} d</td>
                <td style="padding: 6px 8px; color: ${m.daysLt30 > 0 ? '#38bdf8' : '#94a3b8'};">${m.daysLt30} d</td>
                <td style="padding: 6px 8px; color: ${m.daysLt0 > 0 ? '#818cf8' : '#94a3b8'};">${m.daysLt0} d</td>
                <td style="padding: 6px 8px;">${m.windSpeedMph} mph</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;

    this.renderCharts(data);
  }

  private renderCharts(data: ClimatePointData) {
    const ctx365 = document.getElementById('climate-modal-365-chart') as HTMLCanvasElement;
    const ctxSnow = document.getElementById('climate-modal-snowpack-chart') as HTMLCanvasElement;

    if (ctx365) {
      const labels = data.days.map(d => d.doy % 30 === 1 ? d.dateStr.substring(5) : '');
      const tmaxRec = data.days.map(d => d.tmaxRec);
      const tmaxP90 = data.days.map(d => d.tmaxP90);
      const tmaxMean = data.days.map(d => d.tmaxMean);
      const tminMean = data.days.map(d => d.tminMean);
      const tminP90 = data.days.map(d => d.tminP90);
      const tminRec = data.days.map(d => d.tminRec);

      const chart365 = new Chart(ctx365, {
        type: 'line',
        data: {
          labels,
          datasets: [
            { label: 'Record High (°F)', data: tmaxRec, borderColor: '#ef4444', borderWidth: 1.5, pointRadius: 0, fill: false },
            { label: '90% High (°F)', data: tmaxP90, borderColor: '#f97316', borderWidth: 1.5, pointRadius: 0, fill: false },
            { label: 'Avg High (°F)', data: tmaxMean, borderColor: '#eab308', borderWidth: 2, pointRadius: 0, fill: false },
            { label: 'Avg Low (°F)', data: tminMean, borderColor: '#3b82f6', borderWidth: 2, pointRadius: 0, fill: false },
            { label: '90% Low (°F)', data: tminP90, borderColor: '#6366f1', borderWidth: 1.5, pointRadius: 0, fill: false },
            { label: 'Record Low (°F)', data: tminRec, borderColor: '#a855f7', borderWidth: 1.5, pointRadius: 0, fill: false }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { labels: { color: '#94a3b8', font: { size: 10 } } }
          },
          scales: {
            x: { ticks: { color: '#64748b', font: { size: 9 }, maxTicksLimit: 12 }, grid: { color: 'rgba(255,255,255,0.05)' } },
            y: { ticks: { color: '#94a3b8', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.05)' } }
          }
        }
      });
      this.charts.push(chart365);
    }

    if (ctxSnow) {
      const labels = data.weeks.map(w => w.weekLabel);
      const snowDepth = data.weeks.map(w => w.snowDepthIn);
      const snowSwe = data.weeks.map(w => w.snowSweIn);

      const chartSnow = new Chart(ctxSnow, {
        type: 'line',
        data: {
          labels,
          datasets: [
            { label: 'Snowpack Depth (in)', data: snowDepth, borderColor: '#38bdf8', backgroundColor: 'rgba(56, 189, 248, 0.15)', borderWidth: 2, pointRadius: 0, fill: true },
            { label: 'Snow Water Eq (SWE in)', data: snowSwe, borderColor: '#818cf8', backgroundColor: 'rgba(129, 140, 248, 0.25)', borderWidth: 2, pointRadius: 0, fill: true }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { labels: { color: '#94a3b8', font: { size: 10 } } }
          },
          scales: {
            x: { ticks: { color: '#64748b', font: { size: 9 }, maxTicksLimit: 13 }, grid: { color: 'rgba(255,255,255,0.05)' } },
            y: { ticks: { color: '#94a3b8', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.05)' } }
          }
        }
      });
      this.charts.push(chartSnow);
    }
  }
}
